// Mockup "fotografa oturtma" adimlari — TEK KOPYA: sunucu (mockup-render.service)
// ve tarayici (MockupStudioPage) ayni piksel islemlerini kullanir ki Studio'da
// gorulen ile kuyrukta uretilen birebir ayni ciksin.
//
// Sira (tum tasarimlar tek tuval katmaninda birlestikten sonra):
//   1) displaceRGBA — tasarim kumas kirisigi / yuzey kivrimi boyunca egilir
//   2) (isik haritasi: hard-light + dest-in, cagiranda)
//   3) finishRGBA   — kenar yumusatma + fotografin kendi greni
//
// Kayma haritasi sunucuda bir kez uretilir (buildDisplaceMap): R = x egimi,
// G = y egimi, 128 = kayma yok, alfa her zaman opak (tarayici alfasi 0 olan
// pikselin rengini premultiply yuzunden kaybeder).

/** Kayma gucu 100 iken en buyuk kayma: tuvalin en uzun kenarinin bu orani */
export const DISPLACE_MAX_RATIO = 0.006;
/** Dikey kayma yataydan zayif: kumas kivrimlari cogunlukla dikey uzanir */
const DISPLACE_Y = 0.45;

/** Guc (0-100) -> piksel cinsinden en buyuk kayma */
export function displaceAmountPx(strength: number, W: number, H: number): number {
  return (Math.max(0, Math.min(100, strength)) / 100) * DISPLACE_MAX_RATIO * Math.max(W, H);
}

/**
 * Tasarim katmanini kayma haritasina gore yeniden ornekler (alfa agirlikli
 * bilinear — saydam komsunun rengi kenara sizmasin). src ve disp ayni W x H,
 * RGBA. Yeni dizi doner.
 */
export function displaceRGBA(
  src: ArrayLike<number>,
  disp: ArrayLike<number>,
  W: number,
  H: number,
  amountPx: number,
): Uint8ClampedArray {
  const out = new Uint8ClampedArray(W * H * 4);
  if (amountPx <= 0) {
    for (let i = 0; i < out.length; i++) out[i] = src[i];
    return out;
  }
  // Sicak dongu (2000x2000 tarayicida): piksel basina dizi/kapanis ayirmadan
  let r = 0, g = 0, b = 0, al = 0;
  const add = (xx: number, yy: number, wt: number) => {
    if (wt <= 0 || xx < 0 || yy < 0 || xx >= W || yy >= H) return;
    const i = (yy * W + xx) * 4;
    const aa = src[i + 3] * wt;
    if (aa === 0) return;
    r += src[i] * aa; g += src[i + 1] * aa; b += src[i + 2] * aa; al += aa;
  };
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const o = (y * W + x) * 4;
      const dx = (disp[o] - 128) / 127, dy = (disp[o + 1] - 128) / 127;
      if (dx === 0 && dy === 0) {
        out[o] = src[o]; out[o + 1] = src[o + 1]; out[o + 2] = src[o + 2]; out[o + 3] = src[o + 3];
        continue;
      }
      const sx = x + dx * amountPx, sy = y + dy * amountPx * DISPLACE_Y;
      const x0 = Math.floor(sx), y0 = Math.floor(sy);
      const fx = sx - x0, fy = sy - y0;
      r = 0; g = 0; b = 0; al = 0;
      add(x0, y0, (1 - fx) * (1 - fy));
      add(x0 + 1, y0, fx * (1 - fy));
      add(x0, y0 + 1, (1 - fx) * fy);
      add(x0 + 1, y0 + 1, fx * fy);
      if (al <= 0) continue;
      out[o] = r / al; out[o + 1] = g / al; out[o + 2] = b / al; out[o + 3] = al;
    }
  }
  return out;
}

/** Tekrarlanabilir rastgele (mulberry32): sunucu ve tarayici ayni greni basar */
function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Baskiyi fotografa oturtur (yerinde degistirir):
 *  - kenar: alfa 3x3 ortalamayla sinirlanir — yapistirilmis cikartma gibi
 *    keskin kenar yerine baskinin hafif yumusak kenari
 *  - gren: fotografin olculen gurultusu (noiseSigma, 0-255 olcegi) tasarima
 *    eklenir; temiz fotografta ~0 oldugu icin etkisiz
 */
export function finishRGBA(data: Uint8ClampedArray, W: number, H: number, noiseSigma: number): void {
  const alpha = new Uint8ClampedArray(W * H);
  for (let p = 0; p < W * H; p++) alpha[p] = data[p * 4 + 3];
  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const p = y * W + x;
      const a = alpha[p];
      if (a === 0) continue;
      let sum = 0;
      for (let j = -1; j <= 1; j++) for (let i = -1; i <= 1; i++) {
        const xx = x + i, yy = y + j;
        sum += xx < 0 || yy < 0 || xx >= W || yy >= H ? 0 : alpha[yy * W + xx];
      }
      // Ic pikseller (komsular dolu) degismez; yalnizca kenar yumusar
      data[p * 4 + 3] = Math.min(a, Math.round((sum / 9 + a) / 2));
    }
  }
  const sigma = Math.max(0, Math.min(12, noiseSigma));
  if (sigma < 0.5) return;
  const rand = rng(W * 7919 + H);
  for (let p = 0; p < W * H; p++) {
    const o = p * 4;
    if (data[o + 3] === 0) continue;
    // Yaklasik normal dagilim (3 duzgun toplami), parlaklik greni (renksiz)
    const n = ((rand() + rand() + rand()) - 1.5) * 2 * sigma;
    data[o] = data[o] + n; data[o + 1] = data[o + 1] + n; data[o + 2] = data[o + 2] + n;
  }
}
